import { useState, useEffect, useCallback } from 'react';

/**
 * Centralized dark mode management utility
 * Manages theme state, localStorage persistence, and system preference detection
 */
export const useDarkMode = () => {
    // Initialize from existing data-theme attribute (set by blocking script in _document.js)
    // This prevents the white flash by reading what's already set
    const getInitialTheme = () => {
        if (typeof document === 'undefined') return false;
        const existingTheme = document.documentElement.getAttribute('data-theme');
        return existingTheme === 'dark';
    };

    const [isDarkMode, setIsDarkMode] = useState(getInitialTheme);
    const [isThemeInitialized, setIsThemeInitialized] = useState(false);
    const [userHasSetTheme, setUserHasSetTheme] = useState(false);
    const [isClient, setIsClient] = useState(false);

    // Get theme from localStorage
    const getStoredTheme = useCallback(() => {
        if (typeof window === 'undefined') return null;
        return localStorage.getItem('theme');
    }, []);

    // Set theme in localStorage
    const setStoredTheme = useCallback((theme) => {
        if (typeof window === 'undefined') return;
        localStorage.setItem('theme', theme);
    }, []);

    // Check system preference
    const checkSystemTheme = useCallback(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }, []);

    // Set dark mode state
    const setDarkMode = useCallback((dark, isUserAction = false) => {
        setIsDarkMode(dark);
        if (isUserAction) {
            setUserHasSetTheme(true);
        }
        setStoredTheme(dark ? 'dark' : 'light');
        
        // Update document attribute for CSS custom properties
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
        }
    }, [setStoredTheme]);

    // Toggle dark mode
    const toggleDarkMode = useCallback(() => {
        setDarkMode(!isDarkMode, true);
    }, [isDarkMode, setDarkMode]);

    // Reset to system theme
    const resetToSystemTheme = useCallback(() => {
        setUserHasSetTheme(false);
        const systemTheme = checkSystemTheme();
        setDarkMode(systemTheme);
        localStorage.removeItem('theme'); // Remove saved preference
    }, [checkSystemTheme, setDarkMode]);

    // Set client-side flag
    useEffect(() => {
        setIsClient(true);
    }, []);

    // Initialize theme detection
    useEffect(() => {
        if (!isClient) return;

        const initializeTheme = () => {
            // Read existing theme from data-theme attribute (set by blocking script)
            const existingTheme = document.documentElement.getAttribute('data-theme');
            
            // Check localStorage to see if user has set a preference
            const savedTheme = getStoredTheme();
            
            if (savedTheme === 'dark' || savedTheme === 'light') {
                // User has a saved preference
                const isDark = savedTheme === 'dark';
                setIsDarkMode(isDark);
                setUserHasSetTheme(true);
                // Ensure data-theme matches (in case it was changed externally)
                if (existingTheme !== savedTheme) {
                    document.documentElement.setAttribute('data-theme', savedTheme);
                }
            } else {
                // No saved preference - use what blocking script set (or system preference)
                const systemIsDark = checkSystemTheme();
                const currentTheme = existingTheme || (systemIsDark ? 'dark' : 'light');
                const isDark = currentTheme === 'dark';
                setIsDarkMode(isDark);
                setUserHasSetTheme(false);
                // Ensure data-theme is set
                if (!existingTheme) {
                    document.documentElement.setAttribute('data-theme', currentTheme);
                }
            }

            // Listen for system theme changes
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleSystemThemeChange = (e) => {
                // Update theme if user hasn't manually set a preference
                if (!userHasSetTheme) {
                    setDarkMode(e.matches);
                }
            };

            mediaQuery.addEventListener('change', handleSystemThemeChange);

            // Listen for theme changes from other components
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                        const theme = document.documentElement.getAttribute('data-theme');
                        setIsDarkMode(theme === 'dark');
                    }
                });
            });

            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-theme'],
            });

            setIsThemeInitialized(true);

            return () => {
                mediaQuery.removeEventListener('change', handleSystemThemeChange);
                observer.disconnect();
            };
        };

        // Initialize immediately without delay
        initializeTheme();
    }, [isClient, getStoredTheme, setDarkMode, checkSystemTheme, userHasSetTheme]);

    return {
        // Return actual theme state (not false) even before client init
        // The blocking script in _document.js ensures data-theme is set before React
        isDarkMode: typeof document !== 'undefined' 
            ? (document.documentElement.getAttribute('data-theme') === 'dark' || isDarkMode)
            : false,
        isThemeInitialized: isClient ? isThemeInitialized : false,
        setDarkMode,
        toggleDarkMode,
        resetToSystemTheme,
        getStoredTheme,
        setStoredTheme,
        checkSystemTheme,
        isClient,
    };
};

/**
 * Legacy hook for backward compatibility
 * @deprecated Use useDarkMode instead
 */
export const useTheme = useDarkMode;
